import { Flex, IconButton, Skeleton, Text, Tooltip } from "@chakra-ui/react";
import React from "react";
import { RiArrowRightLine } from "react-icons/ri";
import { Link } from "react-router-dom";
import {
  StatusTag,
  StatusTagMetadata,
} from "src/components/DataDisplay/StatusTag";
import { GenericError } from "src/components/Feedback/GenericError";
import { Card } from "src/components/Layout/Card";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { Glossary } from "src/components/Text/Glossary";
import { useGlossary } from "src/hooks/useGlossary";
import * as Format from "src/services/format";
import * as RD from "src/types/remoteData";

const DATA_EXPORT_CARD = "data-export-card";
const DATA_EXPORT_COUNT = "data-export-count";
const DATA_EXPORT_STATUS = "data-export-status";
const DATA_EXPORT_COLOR = "data-export-color";
const DATA_EXPORT_LABEL = "data-export-label";

type Props = {
  total: RD.RemoteData<unknown, number>;
  status: StatusTagMetadata;
  label: string;
  url: string;
};
export const StatusCard: React.FC<Props> = ({ total, status, label, url }) => {
  const { glossary } = useGlossary();
  return (
    <Card
      {...{ [DATA_EXPORT_CARD]: true }}
      showBorder
      display="flex"
      flex="1 1 0px"
      flexDir="column"
      gap="1"
      minW="140px"
    >
      <RemoteDataView
        remoteData={total}
        error={() => <GenericError variant="inline" />}
        loading={<Skeleton width="8rem" height="2rem" />}
        config={{ showDataWhileReloading: false }}
      >
        {(data) => (
          <Text
            fontSize="xl"
            fontWeight="semibold"
            {...{ [DATA_EXPORT_COUNT]: Format.formatWholeNumber(data) }}
          >
            {Format.formatWholeNumber(data)}
          </Text>
        )}
      </RemoteDataView>
      <Flex alignItems="flex-start" flexGrow="1">
        <Flex
          direction="column"
          gap="1"
          grow="1"
          {...{ [DATA_EXPORT_STATUS]: status.label }}
          {...{ [DATA_EXPORT_COLOR]: status.colorScheme }}
        >
          <StatusTag status={status} />
          <Text
            fontSize="xs"
            data-print-hide
            {...{ [DATA_EXPORT_LABEL]: label }}
          >
            <Glossary>{label}</Glossary>
          </Text>
        </Flex>
        <Tooltip label={glossary`Go to ${label.toLowerCase()}`} hasArrow>
          <IconButton
            alignSelf="flex-end"
            as={Link}
            icon={<RiArrowRightLine />}
            variant="ghost"
            aria-label={glossary`Go to ${label.toLowerCase()}`}
            colorScheme="gray"
            to={url}
            data-print-hide
          />
        </Tooltip>
      </Flex>
    </Card>
  );
};

export type StatusCardExportData = {
  count: string;
  status: string;
  label: string;
  colorScheme: string;
};

export function exportStatusCard(
  statusCardElement: HTMLElement
): StatusCardExportData {
  const countEl = statusCardElement.querySelector<HTMLElement>(
    `[${DATA_EXPORT_COUNT}]`
  );
  const statusEl = statusCardElement.querySelector<HTMLElement>(
    `[${DATA_EXPORT_STATUS}]`
  );
  const labelEl = statusCardElement.querySelector<HTMLElement>(
    `[${DATA_EXPORT_LABEL}]`
  );
  const count = countEl?.getAttribute(DATA_EXPORT_COUNT) ?? "";
  const status = statusEl?.getAttribute(DATA_EXPORT_STATUS) ?? "";
  const colorScheme = statusEl?.getAttribute(DATA_EXPORT_COLOR) ?? "gray";
  const label = labelEl?.getAttribute(DATA_EXPORT_LABEL) ?? "";
  return { count, status, colorScheme, label };
}

export function exportStatusCards(
  container: HTMLElement
): StatusCardExportData[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(`[${DATA_EXPORT_CARD}]`)
  ).map(exportStatusCard);
}
